import { Dialog, DialogContent, DialogTitle, IconButton, Stack, Typography, Button, InputAdornment, TextField } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { formatCurrency } from "src/helper/convertNum";
import { FormProvider, useForm, Controller } from "react-hook-form";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import FormControlInput from "src/components/shared/FormControlInput";
import FormControlAutocomplete from "src/components/shared/FormControlAutocomplete";
import { renderBankAccountAutocompleteOption } from "src/components/shared/renderBankAccountAutocompleteOption";
import { useEffect, useMemo, useState } from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import usePaymentAccountList from "src/hooks/debt/usePaymentAccountList";
import convertCurrency from "src/helper/convertCurrency";
import { enqueueSnackbar } from "notistack";
import useDebtPayment from "src/hooks/debt/useDebtPayment";
import useDebounce from "src/hooks/useDebounce";
import { NumericFormat } from "react-number-format";
import { userType } from "src/constant";
import useInfiniteAutocomplete from "src/hooks/useInfiniteAutocomplete";
import dayjs from "dayjs";
const PAYMENT_METHODS = [
  { value: "Transfer", label: "Chuyển khoản" },
  { value: "Cash", label: "Tiền mặt" },
];

const RequiredLabel = ({ children, required = true }) => (
  <span>
    {children}
    {required && <span style={{ color: "red", marginLeft: 2 }}>*</span>}
  </span>
);

const getDebtPaymentAmounts = ({ isMultiplePayment, selectedRow, checkedList, customerOption }) => {
  if (isMultiplePayment) {
    const total = customerOption?.totalDebt ?? checkedList?.reduce((acc, item) => acc + (item?.total ?? 0), 0) ?? 0;
    const balance = customerOption?.balance ?? checkedList?.[0]?.customer?.balance ?? 0;
    return { total, balance, payableDebt: Math.abs(total - balance) };
  }
  const total = selectedRow?.total ?? 0;
  const balance = selectedRow?.balance ?? selectedRow?.customer?.balance ?? 0;
  return { total, balance, payableDebt: Math.abs(total - balance) };
};

const schema = yup.object().shape({
  transferDate: yup
    .mixed()
    .required("Ngày đi tiền trên nội dung chuyển khoản là bắt buộc")
    .test("valid-date", "Ngày không hợp lệ", (v) => v != null && dayjs(v).isValid()),
  amount: yup.number().required("Số tiền thanh toán là bắt buộc").typeError("Số tiền thanh toán phải là số").min(1, "Số tiền thanh toán phải lớn hơn 0"),
  paymentMethod: yup
    .object({
      value: yup.string().required("Hình thức thanh toán là bắt buộc"),
    })
    .required("Hình thức thanh toán là bắt buộc"),
  account: yup
    .object({
      value: yup.string().required("Số tài khoản nhận thanh toán là bắt buộc"),
    })
    .required("Số tài khoản nhận thanh toán là bắt buộc"),
  customer: yup
    .object({
      value: yup.string().required("Khách hàng là bắt buộc"),
    })
    .when("isMultiplePayment", {
      is: true,
      then: (schema) => schema.required("Khách hàng là bắt buộc"),
      otherwise: (schema) => schema.notRequired(),
    }),
  note: yup.string().optional(),
  isMultiplePayment: yup.boolean().notRequired(),
  total: yup.number().notRequired(),
});

export default function DebtSalesPaymentDialog({ open, onClose, selectedRow = null, title, checkedList = [], isMultiplePayment = false }) {
  const [searchCustomer, setSearchCustomer] = useState("");
  const [searchSales, setSearchSales] = useState("");
  const debouncedSearchCustomer = useDebounce(searchCustomer, 500);
  const debouncedSearchSales = useDebounce(searchSales, 500);
  const { data: bankInfoList } = usePaymentAccountList({ warehouse: isMultiplePayment ? undefined : selectedRow?.warehouse?._id });
  const { trigger: updateDebt } = useDebtPayment();
  const [warningCustomerMessage, setWarningCustomerMessage] = useState("");
  const [autocompleteKey, setAutocompleteKey] = useState(0);
  const form = useForm({
    defaultValues: {
      transferDate: dayjs(),
      amount: "",
      paymentMethod: null,
      account: null,
      customer: null,
      note: "",
      isMultiplePayment: false,
      total: 0,
      sales: null,
    },
    resolver: yupResolver(schema),
  });
  const { reset, watch } = form;
  const { amount, sales: salesValue, customer: customerValue } = watch();

  const salesAutocompleteEnabled = isMultiplePayment && open;
  const {
    items: salesItems,
    isLoading: salesLoading,
    loadingMore: salesLoadingMore,
    hasMore: salesHasMore,
    onLoadMore: onLoadMoreSales,
  } = useInfiniteAutocomplete(
    "/api/v1/users",
    { limit: 10, type: userType.sales, searchField: "fullName", searchValue: debouncedSearchSales || undefined },
    salesAutocompleteEnabled,
    debouncedSearchSales,
  );

  const customerAutocompleteEnabled = isMultiplePayment && open && !!salesValue?.value;
  const {
    items: customerItems,
    isLoading: customerLoading,
    loadingMore: customerLoadingMore,
    hasMore: customerHasMore,
    onLoadMore: onLoadMoreCustomer,
  } = useInfiniteAutocomplete(
    "/api/v1/customers",
    {
      limit: 10,
      searchField: "fullName",
      searchValue: debouncedSearchCustomer || undefined,
      sales: salesValue?.value,
    },
    customerAutocompleteEnabled,
    `${salesValue?.value ?? ""}-${debouncedSearchCustomer}`,
  );

  const checkedListIds = useMemo(() => (Array.isArray(checkedList) ? checkedList.map((i) => i?._id).join(",") : ""), [checkedList]);

  const resetAutocompleteState = () => {
    setSearchCustomer("");
    setSearchSales("");
    setWarningCustomerMessage("");
    setAutocompleteKey((prev) => prev + 1);
  };

  const handleClose = () => {
    resetAutocompleteState();
    reset({
      transferDate: dayjs(),
      amount: "",
      paymentMethod: null,
      account: null,
      customer: null,
      note: "",
      isMultiplePayment: false,
      total: 0,
      sales: null,
    });
    onClose?.();
  };

  useEffect(() => {
    if (!open) return;
    const { payableDebt } = getDebtPaymentAmounts({ isMultiplePayment, selectedRow, checkedList });
    reset({
      transferDate: dayjs(),
      amount: "",
      paymentMethod: null,
      account: null,
      customer: null,
      note: "",
      isMultiplePayment: Boolean(isMultiplePayment),
      total: payableDebt,
      sales: null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- chỉ reset khi mở dialog / đổi phiếu
  }, [open, selectedRow?._id, isMultiplePayment, checkedListIds, reset]);

  useEffect(() => {
    if (isMultiplePayment && checkedList?.length) {
      const first = checkedList?.[0];
      if (first?.customer?._id) {
        form.setValue("customer", {
          value: first?.customer?._id,
          label: first?.customer?.fullName ?? "",
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkedList, isMultiplePayment]);

  const handleChangeCustomer = (option) => {
    const first = checkedList?.[0];
    if (first?.customer?._id && option?.value !== first?.customer?._id) {
      setWarningCustomerMessage("Lưu ý: chọn khách hàng khác đề xuất, đồng nghĩa thanh toán tất cả hoá đơn theo khách hàng bạn đã thay đổi");
    } else {
      setWarningCustomerMessage("");
    }
  };

  const handleInputChangeCustomer = (_, value, reason) => {
    if (reason === "reset") return;
    if (reason === "clear") {
      setSearchCustomer("");
      return;
    }
    setSearchCustomer(value);
  };

  const handleInputChangeSales = (_, value, reason) => {
    if (reason === "reset") return;
    if (reason === "clear") {
      setSearchSales("");
      return;
    }
    setSearchSales(value);
  };

  const handleSubmit = async (values) => {
    try {
      const customerId = isMultiplePayment ? values?.customer?.value : selectedRow?.customer?._id;
      if (!customerId) {
        enqueueSnackbar("Không tìm thấy thông tin khách hàng để thanh toán", { variant: "error", autoHideDuration: 3000 });
        return;
      }

      const body = {
        method: values?.paymentMethod?.value,
        totalReceived: values?.amount,
        paymentDate: dayjs(values?.transferDate).toISOString(),
        note: values?.note,
        customer: customerId,
        innCodes: isMultiplePayment ? checkedList?.map((item) => item?._id) : [selectedRow?._id],
        bankInfo: values?.account?.value,
        sales: values?.sales?.value,
      };

      const res = await updateDebt({
        body,
      });

      if (res?.success) {
        enqueueSnackbar("Thanh toán công nợ thành công", { variant: "success", autoHideDuration: 3000 });
        handleClose();
        return;
      }
      enqueueSnackbar(res?.error?.message ?? "Thanh toán công nợ thất bại", { variant: "error", autoHideDuration: 3000 });
    } catch (error) {
      enqueueSnackbar("Thanh toán công nợ thất bại", { variant: "error", autoHideDuration: 3000 });
    }
  };

  const dataRender = useMemo(() => {
    const { total, balance, payableDebt } = getDebtPaymentAmounts({
      isMultiplePayment,
      selectedRow,
      checkedList,
      customerOption: customerValue,
    });
    return {
      salesStaffName: isMultiplePayment ? (checkedList?.[0]?.sales?.fullName ?? "-") : (selectedRow?.sales?.fullName ?? "-"),
      customerName: isMultiplePayment ? (checkedList?.[0]?.customer?.fullName ?? "-") : (selectedRow?.customer?.fullName ?? "-"),
      total,
      balance,
      payableDebt,
    };
  }, [checkedList, isMultiplePayment, selectedRow, customerValue]);

  const accountOptions = useMemo(() => {
    return bankInfoList?.data?.map((bankInfo) => ({
      value: bankInfo?._id,
      label: [bankInfo?.bankName, bankInfo.ownerName, bankInfo?.accountNumber].filter(Boolean).join(" - "),
      logo: bankInfo?.bank?.logo ?? bankInfo?.logo ?? null,
    }));
  }, [bankInfoList]);

  const customerOptions = useMemo(() => {
    return customerItems.map((customer) => ({
      value: customer?._id,
      label: customer?.fullName,
      totalDebt: customer?.totalDebt,
      balance: customer?.balance,
    }));
  }, [customerItems]);

  const salesOptions = useMemo(() => {
    return salesItems.map((sale) => ({
      value: sale?._id,
      label: sale?.fullName,
    }));
  }, [salesItems]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: "16px" } }}
    >
      <DialogTitle sx={{ pb: 1, fontWeight: "bold", px: 3 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          {title}
          <IconButton
            aria-label="close"
            onClick={handleClose}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ pt: 0, pb: 3, px: 3 }}>
        <FormProvider {...form}>
          <Stack spacing={2}>
            <Stack spacing={0.5}>
              <Typography
                fontSize={12}
                color="#989898"
              >
                Tên nhân viên kinh doanh
              </Typography>
              {isMultiplePayment ? (
                <FormControlAutocomplete
                  key={`sales-${autocompleteKey}`}
                  name="sales"
                  placeholder="Chọn nhân viên kinh doanh"
                  options={salesOptions}
                  size="small"
                  loading={salesLoading}
                  hasMore={salesHasMore}
                  loadingMore={salesLoadingMore}
                  onLoadMore={onLoadMoreSales}
                  filterOptions={(opts) => opts}
                  onInputChange={handleInputChangeSales}
                  onChange={() => form.setValue("customer", null)}
                />
              ) : (
                <Typography fontWeight="bold">{dataRender?.salesStaffName}</Typography>
              )}
            </Stack>
            <Stack spacing={0.5}>
              <Typography
                fontSize={12}
                color="#989898"
              >
                <RequiredLabel required={isMultiplePayment}>Tên Khách hàng</RequiredLabel>
              </Typography>
              {isMultiplePayment ? (
                <>
                  <FormControlAutocomplete
                    key={`customer-${autocompleteKey}`}
                    name="customer"
                    placeholder="Chọn khách hàng"
                    options={customerOptions}
                    size="small"
                    loading={customerLoading}
                    hasMore={customerHasMore}
                    loadingMore={customerLoadingMore}
                    onLoadMore={onLoadMoreCustomer}
                    filterOptions={(opts) => opts}
                    onChange={handleChangeCustomer}
                    onInputChange={handleInputChangeCustomer}
                    disabled={!salesValue?.value}
                  />
                  {warningCustomerMessage && (
                    <Typography
                      fontSize={12}
                      color="error"
                    >
                      {warningCustomerMessage}
                    </Typography>
                  )}
                </>
              ) : (
                <Typography fontWeight="bold">{dataRender?.customerName}</Typography>
              )}
            </Stack>
            <Stack spacing={0.5}>
              <Typography
                fontSize={12}
                color="#989898"
              >
                Tổng công nợ cần thanh toán
              </Typography>
              <Typography
                fontWeight="bold"
                color="error"
              >
                {formatCurrency(dataRender?.payableDebt ?? 0)}
              </Typography>
            </Stack>
            <Stack spacing={0.5}>
              <Typography
                fontSize={12}
                color="#989898"
              >
                Số dư chưa gạt nợ:
              </Typography>
              <Typography fontWeight="bold">{formatCurrency(dataRender?.balance ?? 0)}</Typography>
            </Stack>

            <Stack spacing={1}>
              <Typography
                fontSize={12}
                color="#989898"
              >
                <RequiredLabel>Ngày đi tiền trên nội dung chuyển khoản</RequiredLabel>
              </Typography>
              <Controller
                control={form.control}
                name="transferDate"
                render={({ field, formState: { errors } }) => (
                  <DatePicker
                    format="DD/MM/YYYY"
                    slotProps={{
                      textField: {
                        size: "small",
                        error: !!errors?.transferDate,
                        helperText: errors?.transferDate?.message,
                      },
                    }}
                    value={dayjs.isDayjs(field.value) ? field.value : field.value ? dayjs(field.value) : null}
                    onChange={(newValue) => field.onChange(newValue ?? null)}
                  />
                )}
              />
            </Stack>
            <Stack spacing={1}>
              <Typography
                fontSize={12}
                color="#989898"
              >
                <RequiredLabel>Nhập số tiền thanh toán</RequiredLabel>
              </Typography>
              <Controller
                control={form.control}
                name="amount"
                render={({ field, formState: { errors } }) => (
                  <NumericFormat
                    fullWidth
                    InputProps={{
                      inputProps: { min: 0 },
                      startAdornment: (
                        <InputAdornment position="start">
                          <Typography>đ{field?.value ? "" : 0}</Typography>
                        </InputAdornment>
                      ),
                    }}
                    thousandSeparator
                    customInput={TextField}
                    size="small"
                    error={!!errors?.amount}
                    helperText={errors?.amount?.message || ""}
                    value={field.value}
                    onValueChange={(values) => field.onChange(values.floatValue ?? "")}
                    onInput={(e) => {
                      if (e.target.value.length > 10) {
                        e.target.value = e.target.value.slice(0, 10);
                      }
                    }}
                  />
                )}
              />
              <Typography
                fontSize={12}
                color="#989898"
              >
                {convertCurrency(amount)} đồng
              </Typography>
            </Stack>
            <Stack spacing={1}>
              <Typography
                fontSize={12}
                color="#989898"
              >
                <RequiredLabel>Hình thức thanh toán</RequiredLabel>
              </Typography>
              <FormControlAutocomplete
                name="paymentMethod"
                options={PAYMENT_METHODS}
                size="small"
                fullWidth
              />
            </Stack>
            <Stack spacing={1}>
              <Typography
                fontSize={12}
                color="#989898"
              >
                <RequiredLabel>Số tài khoản nhận thanh toán</RequiredLabel>
              </Typography>
              <FormControlAutocomplete
                name="account"
                options={accountOptions}
                size="small"
                fullWidth
                renderOption={renderBankAccountAutocompleteOption}
              />
            </Stack>
            <Stack spacing={1}>
              <Typography
                fontSize={12}
                color="#989898"
              >
                Ghi chú
              </Typography>
              <FormControlInput
                name="note"
                placeholder="Ghi chú..."
                multiline
                minRows={3}
                size="small"
                fullWidth
              />
            </Stack>
            <Button
              fullWidth
              variant="contained"
              onClick={form.handleSubmit(handleSubmit)}
            >
              Xác nhận thanh toán
            </Button>
          </Stack>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
}
